import { useTranslation } from "react-i18next";

import type { EmployerHistoryRow } from "../api/worknumber.types";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface PurchasedEmployerInfoPanelProperties {
  panelId: string;
  /** Pulled / previously-pulled employer work history rows. */
  rows: EmployerHistoryRow[];
}

// Column order matches the Figma design and the OR-22604 spec.
const COLUMN_KEYS: Array<keyof EmployerHistoryRow> = [
  "employerName",
  "employeeStatus",
  "occupation",
  "hireDate",
  "payFrequency",
  "payRate",
  "payPeriodFrequency",
];

// ---------------------------------------------------------------------------
// PurchasedEmployerInfoPanel
// Multi-row work-history table, styled like the income Calculator results
// (IncomeTaxesPanel). One row per pulled employment record.
// ---------------------------------------------------------------------------
export function PurchasedEmployerInfoPanel({ panelId, rows }: PurchasedEmployerInfoPanelProperties) {
  const { t } = useTranslation();

  return (
    <div id={panelId} className="bg-surface-subtle border-primary/30 w-fit rounded-md border px-3 py-3 shadow-sm">
      <div className="bg-card w-fit overflow-x-auto rounded-md">
        <table className="w-auto text-xs">
          <caption className="sr-only">{t("review.income.employerHistory.title")}</caption>
          <thead>
            <tr className="bg-surface-subtle border-border border-b">
              {COLUMN_KEYS.map((key, columnIndex) => (
                <th
                  key={key}
                  scope="col"
                  className={`text-muted-foreground px-5 py-1.5 text-center font-medium ${columnIndex < COLUMN_KEYS.length - 1 ? "border-border border-r" : ""}`}
                >
                  {t(`review.income.employerHistory.columns.${key}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="text-foreground">
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex} className="border-border border-t first:border-t-0">
                {COLUMN_KEYS.map((key, columnIndex) => (
                  <td
                    key={key}
                    className={`px-5 py-1.5 text-center ${columnIndex < COLUMN_KEYS.length - 1 ? "border-border border-r" : ""}`}
                  >
                    {row[key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
